import { createFileRoute } from '@tanstack/react-router';
import { requireAuth } from '../features/auth/guards.js';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/account/PageHeader.js';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Check } from 'lucide-react';
import { fetchKycStatus, KYC_QUERY_KEYS, KYC_DOCUMENT_TYPES, type KycDocument } from '@/features/kyc/api.js';
import { KycStatusBanner } from '@/features/kyc/components/KycStatusBanner.js';
import { KycRejectionAlert } from '@/features/kyc/components/KycRejectionAlert.js';
import { DocumentUploadCard } from '@/features/kyc/components/DocumentUploadCard.js';

export const Route = createFileRoute('/_player/kyc')({
  beforeLoad: requireAuth,
  component: KycScreen,
});

function KycSkeleton() {
  return (
    <div className="flex flex-col gap-4 animate-pulse">
      <div className="h-16 bg-[var(--bg-elevated)]/50 rounded-xl" />
      <div className="h-40 bg-[var(--bg-elevated)]/50 rounded-xl" />
    </div>
  );
}

function KycScreen() {
  const { t } = useTranslation('kyc');

  const kycQuery = useQuery({
    queryKey: KYC_QUERY_KEYS.status,
    queryFn: fetchKycStatus,
    staleTime: 30_000,
  });

  if (kycQuery.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('title')} subtitle={t('subtitle')} />
        <KycSkeleton />
      </div>
    );
  }

  if (kycQuery.isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('title')} subtitle={t('subtitle')} />
        <div className="py-10 text-center text-[var(--danger)] flex flex-col items-center gap-3">
          Failed to load KYC status.
          <Button size="sm" variant="secondary" onClick={() => kycQuery.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const { status, documents, rejectionReason } = kycQuery.data!;

  // Index existing documents by type for easy lookup.
  const docsByType = new Map<string, KycDocument>();
  for (const d of documents) {
    // Keep the most recent one if duplicates exist (should not happen in practice).
    if (!docsByType.has(d.type) || d.uploadedAt > docsByType.get(d.type)!.uploadedAt) {
      docsByType.set(d.type, d);
    }
  }

  const canEdit = status === 'unsubmitted' || status === 'rejected';
  const allRequiredUploaded = KYC_DOCUMENT_TYPES.every((type) => docsByType.has(type));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      {/* ── State: Approved ── */}
      {status === 'approved' && (
        <Card className="p-6 flex flex-col items-center gap-3 text-center">
          <div className="w-16 h-16 rounded-full bg-[var(--success-bg)] grid place-items-center text-[var(--success)]">
            <Check className="size-8" aria-hidden />
          </div>
          <h2 className="text-xl font-bold text-[var(--success)]">{t('status.approved')}</h2>
          <p className="text-[13px] text-[var(--text-secondary)] max-w-xs">
            {t('subtitle')}
          </p>
        </Card>
      )}

      {/* ── State: Unsubmitted — explain & CTA ── */}
      {status === 'unsubmitted' && documents.length === 0 && (
        <Card className="p-5 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-[16px] font-bold">{t('title')}</h2>
            <p className="text-[13px] text-[var(--text-secondary)]">{t('subtitle')}</p>
          </div>
          <ul className="flex flex-col gap-2 text-[13px] text-[var(--text-secondary)]">
            {KYC_DOCUMENT_TYPES.map((type) => (
              <li key={type} className="flex items-center gap-2">
                <span className="text-[var(--primary)]">·</span>
                {t(`documentTypes.${type}`)}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ── Status banner ── */}
      {status !== 'approved' && <KycStatusBanner status={status} />}

      {/* ── Rejection reason ── */}
      {status === 'rejected' && rejectionReason && (
        <KycRejectionAlert reason={rejectionReason} />
      )}

      {/* ── Document upload cards ── */}
      {status !== 'approved' && (
        <section className="flex flex-col gap-3">
          <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
            Documents
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {KYC_DOCUMENT_TYPES.map((type) => (
              <DocumentUploadCard
                key={type}
                type={type}
                document={docsByType.get(type) ?? null}
                canEdit={canEdit}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Submit CTA when all uploaded and status is pending ── */}
      {status === 'pending' && allRequiredUploaded && (
        <Card className="p-4 bg-[var(--info-bg)] border-[var(--info)]/30 flex items-center justify-between gap-4">
          <p className="text-[13px] text-[var(--info)] font-semibold">
            {t('status.pending')}
          </p>
        </Card>
      )}

      {/* ── Upload hint when editing ── */}
      {canEdit && !allRequiredUploaded && (
        <p className="text-[12px] text-[var(--text-muted)] text-center">
          Upload all 4 document types to complete your verification.
        </p>
      )}
    </div>
  );
}
